import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { far } from '@fortawesome/pro-regular-svg-icons';

// Services
import PropTypes from "prop-types";

const Wizard = (props) => {
    
    // Step data
    const [currentStep, setCurrentStep] = useState(0);
    const [displayStep, setDisplayStep] = useState(1);
    const [stepAmount, setStepAmount] = useState(1);
    const [progress, setProgress] = useState(0);
    
    // Content arrays
    const [titles, setTitles] = useState([]);
    const [returnTexts, setReturnTexts] = useState([]);
    const [bodyContents, setBodyContents] = useState([]);
    const [footerContents, setFooterContents] = useState([]);
    const [closeFunctions, setCloseFunctions] = useState([]);
    const [backFunctions, setBackFunctions] = useState([]);
    const [nextFunctions, setNextFunctions] = useState([]);
    
    // Handle steps data
    useEffect(() => {
    
        if (props.steps.length > 0) {
    
            // Set the amount of steps
            setStepAmount(props.steps.length);
            
            let titleArray = [];
            let returnTextArray = [];
            let bodyContentArray = [];
            let footerContentArray = [];
            let closeFunctionArray = [];
            let backFunctionArray = [];
            let nextFunctionArray = [];
    
            // Collect all steps data
            for (const step of props.steps) {
                titleArray.push(step.title);
                returnTextArray.push(step.returnText);
                bodyContentArray.push(step.bodyContent);
                footerContentArray.push(step.footerContent);
                closeFunctionArray.push(step.closeFunction);
                backFunctionArray.push(step.backFunction ?? step.closeFunction); // use closeFunction by default
                nextFunctionArray.push(step.nextFunction ?? null); // no forward function by default
            }
    
            // Set all steps data
            setTitles(titleArray);
            setReturnTexts(returnTextArray);
            setBodyContents(bodyContentArray);
            setFooterContents(footerContentArray);
            setCloseFunctions(closeFunctionArray);
            setBackFunctions(backFunctionArray);
            setNextFunctions(nextFunctionArray);
        }
        
    }, [props.steps]);
    
    // Update step
    useEffect(() => {
        
        // Get the current step
        if (typeof props.currentStep !== 'undefined') {
            setCurrentStep(props.currentStep);
        } else {
            setCurrentStep(0);
        }
    
        // Set the displayable step number and progress percentage
        setDisplayStep(currentStep + 1);
        setProgress(Math.ceil((displayStep / stepAmount) * 100));
        
    }, [props.currentStep, currentStep, displayStep, stepAmount]);
    
    return (
        <>
            {/* Display wizard if current step is equal or bigger than zero */}
            { currentStep >= 0 && 
                (<Modal
                    show={ true }
                    onHide={() => closeFunctions[currentStep]?.()}
                    aria-labelledby="wizard-modal-title"
                    aria-describedby="wizard-modal-body"
                    aria-modal="true"
                >
                    {/* Header */}
                    <Modal.Header className="mt-3 align-items-start" closeButton>
                        {/* Closing button */}
                        <Button
                            className='modal-back'
                            onClick={() => closeFunctions[currentStep]?.()}
                            aria-label={props.closeText}
                        >
                            <FontAwesomeIcon icon={ far.faArrowLeft } /> { returnTexts[currentStep] }
                        </Button>
                        {/* Title */}
                        <Modal.Title tabIndex={0}>{ titles[currentStep] }</Modal.Title>

                        {/* Step numbers mobile only */}
                        <div className='modal-steps d-sm-none'>
                            {displayStep}<span className={'text-lighter'}>/{stepAmount}</span>
                        </div>

                        {/* Steps*/}
                        <ButtonGroup className='d-none d-sm-flex align-items-center pt-1'>
                            <Button
                                variant="browse"
                                className="browse-job pe-1"
                                onClick={() => backFunctions[currentStep]?.()}
                                aria-label={props.previousText}
                            >
                                <FontAwesomeIcon icon={far.faChevronLeft} aria-hidden="true" className="me-2" /> {displayStep}
                            </Button>
                            <span>/</span>
                            <Button variant="browse"
                                    className="browse-job ps-1"
                                    onClick={() => nextFunctions[currentStep]?.()}
                                    aria-label={props.nextStep}
                            >
                                {stepAmount} 
                                <FontAwesomeIcon icon={far.faChevronRight} className="ms-2" aria-hidden="true" />
                            </Button>
                        </ButtonGroup>
                    </Modal.Header>
                    
                    {/* Body */}
                    <Modal.Body id="wizard-modal-body" tabIndex={0}>
                        { bodyContents[currentStep] }
                    </Modal.Body>
                    
                    {/* Footer */}
                    <Modal.Footer>
                        { footerContents[currentStep] }
                    </Modal.Footer>
                    
                    {/* Progress bar */}
                    <div className='wizard-progress-container'>
                        <div
                            className={'wizard-progress ' + (progress > 99 ? 'wizard-progress-full' : '')}
                            style={{width: progress + '%'}}
                            aria-hidden="true"
                        />
                    </div>
                </Modal>)
            }
        </>
    );
};

// Property validation
Wizard.propTypes = {
    currentStep: PropTypes.number.isRequired,
    steps: PropTypes.array.isRequired,
    previousText: PropTypes.string,
    nextStep: PropTypes.string,
    closeText: PropTypes.string,
};

export default Wizard;